// client/src/components/admin/AdminBidDetails.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const AdminBidDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bid, setBid] = useState(null);
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionInProgress, setActionInProgress] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const fetchBidDetails = async () => {
      try {
        // Use the correct endpoint based on the server-side route configuration
        const bidRes = await axios.get(`/api/bids/${id}`);
        
        if (bidRes.data && (bidRes.data.bid || bidRes.data)) {
          const bidData = bidRes.data.bid || bidRes.data;
          setBid(bidData);
          
          // Try to get the parent request if it's not already populated
          if (bidData.request) {
            try {
              // Check if request is already a populated object with an id field
              const requestId = typeof bidData.request === 'object' && (bidData.request.id || bidData.request._id) 
                ? bidData.request.id || bidData.request._id 
                : bidData.request;
              
              // Only fetch the request if we have a request ID that's a string (not an object)
              if (typeof requestId === 'string') {
                const requestRes = await axios.get(`/api/requests/${requestId}`);
                if (requestRes.data && requestRes.data.request) {
                  setRequest(requestRes.data.request);
                } else if (requestRes.data) {
                  // Handle case where the response might not have a 'request' property
                  setRequest(requestRes.data);
                }
              } else if (typeof bidData.request === 'object') {
                // If request is already populated, just use it
                setRequest(bidData.request);
              }
            } catch (requestErr) {
              console.warn('Could not fetch parent request:', requestErr);
              // Non-critical error
            }
          }
          
          setLoading(false);
          return;
        }
        
        throw new Error('Invalid response format');
      } catch (err) {
        console.error('Error fetching bid details:', err);
        
        // Try fallback method - fetch from admin bids endpoint
        try {
          const adminBidsRes = await axios.get('/api/bids/admin/bids');
          
          if (adminBidsRes.data && (adminBidsRes.data.bids || adminBidsRes.data)) {
            const allBids = adminBidsRes.data.bids || adminBidsRes.data;
            
            if (Array.isArray(allBids)) {
              // Find the matching bid
              const foundBid = allBids.find(b => 
                b.id === id || b._id === id || 
                (b._id && b._id.toString() === id) || 
                (b.id && b.id.toString() === id)
              );
              
              if (foundBid) {
                setBid(foundBid);
                
                // Try to get the parent request
                if (foundBid.request) {
                  // Similar logic as above for getting the request
                  const requestId = typeof foundBid.request === 'object' 
                    ? foundBid.request.id || foundBid.request._id 
                    : foundBid.request;
                  
                  if (typeof requestId === 'string') {
                    const requestRes = await axios.get(`/api/requests/${requestId}`);
                    if (requestRes.data && requestRes.data.request) {
                      setRequest(requestRes.data.request);
                    } else if (requestRes.data) {
                      setRequest(requestRes.data);
                    }
                  } else if (typeof foundBid.request === 'object') {
                    setRequest(foundBid.request);
                  }
                }
                
                setLoading(false);
                return;
              }
            }
          }
          
          // If we reach here, we couldn't find the bid in the admin bids
          throw new Error('Bid not found in admin bids');
        } catch (fallbackErr) {
          console.error('Error in fallback method:', fallbackErr);
          setError('Failed to fetch bid details. Please ensure you have the correct permissions.');
          setLoading(false);
        }
      }
    };
    
    fetchBidDetails();
  }, [id]);

  const handleUpdateStatus = async (newStatus) => {
    setActionInProgress(true);
    setError('');
    setSuccessMessage('');
    
    try {
      // Use the correct endpoint for updating bid status
      const response = await axios.put(`/api/bids/${id}/status`, { status: newStatus });
      
      // If the request was successful, update the bid in state
      if (response.data && response.data.success) {
        setBid({ ...bid, status: newStatus });
        setSuccessMessage(`Bid ${newStatus === 'approved' ? 'approved' : 'rejected'} successfully`);
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      } else {
        // Try fallback - for cancellation
        if (newStatus === 'rejected') {
          try {
            await axios.post(`/api/bids/cancel/${id}`);
            
            // Update the bid in state
            setBid({ ...bid, status: 'rejected' });
            setSuccessMessage('Bid rejected successfully');
            
            // Clear success message after 3 seconds
            setTimeout(() => {
              setSuccessMessage('');
            }, 3000);
          } catch (cancelErr) {
            setError(cancelErr.response?.data?.message || 'Failed to reject bid');
          }
        } else {
          setError(response.data?.message || `Failed to ${newStatus} bid`);
        }
      }
    } catch (err) {
      console.error(`Error updating bid status to ${newStatus}:`, err);
      setError(err.response?.data?.message || `Failed to ${newStatus} bid. Please try again.`);
    } finally {
      setActionInProgress(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    if (amount === undefined || amount === null) return 'N/A';
    return `${currency} ${(Number(amount) || 0).toLocaleString('en-US')}`;
  };

  if (loading) {
    return <div className="loading">Loading bid details...</div>;
  }

  if (error && !bid) {
    return <div className="error-message">{error}</div>;
  }

  if (!bid) {
    return <div className="error-message">Bid not found</div>;
  }

  // Get bid ID for display
  const bidId = bid._id || bid.id || id;

  return (
    <div className="admin-container">
      <div className="admin-header">
        <button 
          onClick={() => navigate(-1)} 
          className="btn btn-light"
        >
          &larr; Back
        </button>
        <h2 className="admin-page-title">Bid Details #{bidId}</h2>
      </div>
      
      {error && <div className="alert-danger">{error}</div>}
      {successMessage && <div className="alert-success">{successMessage}</div>}
      
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Bid Information</h3>
          <span className={`status status-${bid.status === 'approved' ? 'approved' : bid.status === 'rejected' ? 'rejected' : 'pending'}`}>
            {bid.status || 'pending'}
          </span>
        </div>
        <div className="admin-card-body">
          <div className="admin-detail-row">
            <div className="admin-detail-label">Bid ID</div>
            <div className="admin-detail-value">#{bidId}</div>
          </div>
          
          <div className="admin-detail-row">
            <div className="admin-detail-label">Bank</div>
            <div className="admin-detail-value">
              {bid.bank ? (
                typeof bid.bank === 'object' ? (
                  <Link to={`/admin/organizations/${bid.bank._id || bid.bank.id || ''}`}>
                    {bid.bank.name || 'View Bank'}
                  </Link>
                ) : (
                  <Link to={`/admin/organizations/${bid.bank}`}>
                    View Bank
                  </Link>
                )
              ) : 'N/A'}
            </div>
          </div>

          <div className="admin-detail-row">
            <div className="admin-detail-label">Currency</div>
            <div className="admin-detail-value">{bid.currency || 'EUR'}</div>
          </div>
          
          <div className="admin-detail-row">
            <div className="admin-detail-label">Bid Amount</div>
            <div className="admin-detail-value">{formatCurrency(bid.amount, bid.currency)}</div>
          </div>

          <div className="admin-detail-row">
            <div className="admin-detail-label">Minimum Allocation</div>
            <div className="admin-detail-value">{formatCurrency(bid.minimumAllocation, bid.currency)}</div>
          </div>
          
          <div className="admin-detail-row">
            <div className="admin-detail-label">Interest Rate</div>
            <div className="admin-detail-value">{bid.interestRate || 0}%</div>
          </div>
          
          <div className="admin-detail-row">
            <div className="admin-detail-label">Rate Type</div>
            <div className="admin-detail-value">{bid.rateType || 'Fixed'}</div>
          </div>

          {bid.rateType === 'floating' && bid.baseRate && (
          <div className="admin-detail-row">
            <div className="admin-detail-label">Base Rate</div>
            <div className="admin-detail-value">
              {bid.baseRate === 'euribor_1w' && 'Euribor 1 Week'}
              {bid.baseRate === 'euribor_1m' && 'Euribor 1 Month'}
              {bid.baseRate === 'euribor_3m' && 'Euribor 3 Months'}
              {bid.baseRate === 'euribor_6m' && 'Euribor 6 Months'}
              {bid.baseRate === 'euribor_12m' && 'Euribor 12 Months'}
              {bid.baseRate === 'estr' && 'ESTR'}
              {bid.baseRate === 'sofr_1m' && 'SOFR 1 Month'}
              {bid.baseRate === 'sofr_3m' && 'SOFR 3 Months'}
              {bid.baseRate === 'sofr_6m' && 'SOFR 6 Months'}
              {bid.baseRate === 'sofr_12m' && 'SOFR 12 Months'}
              {bid.baseRate === 'sonia_1m' && 'SONIA 1 Month'}
              {bid.baseRate === 'sonia_3m' && 'SONIA 3 Months'}
              {bid.baseRate === 'sonia_6m' && 'SONIA 6 Months'}
              {bid.baseRate === 'sonia_12m' && 'SONIA 12 Months'}
              {bid.baseRate === 'saron_1m' && 'SARON 1 Month'}
              {bid.baseRate === 'saron_3m' && 'SARON 3 Months'}
              {bid.baseRate === 'saron_6m' && 'SARON 6 Months'}
              {bid.baseRate === 'saron_12m' && 'SARON 12 Months'}
              {bid.baseRate === 'bbsy_1m' && 'BBSY 1 Month'}
              {bid.baseRate === 'bbsy_3m' && 'BBSY 3 Months'}
              {bid.baseRate === 'bbsy_6m' && 'BBSY 6 Months'}
              {bid.baseRate === 'corra_1m' && 'CORRA 1 Month'}
              {bid.baseRate === 'corra_3m' && 'CORRA 3 Months'}
              {bid.baseRate === 'bkbm_1m' && 'BKBM 1 Month'}
              {bid.baseRate === 'bkbm_3m' && 'BKBM 3 Months'}
              {bid.baseRate === 'bkbm_6m' && 'BKBM 6 Months'}
            </div>
          </div>
        )}    

          <div className="admin-detail-row">
            <div className="admin-detail-label">Notice Period</div>
            <div className="admin-detail-value">
              {bid.noticePeriod ? `${bid.noticePeriod} days` : 'N/A'}
            </div>
          </div>

          <div className="admin-detail-row">
            <div className="admin-detail-label">Lock-in Period</div>
            <div className="admin-detail-value">
              {request?.duration === 'overnight' && bid.lockInPeriod ? `${bid.lockInPeriod} ${bid.lockInPeriod === 1 ? 'day' : 'days'}`
              : 'N/A'}
             </div>
          </div>

          {/* Early Withdrawal Penalty Fields */}
          {request?.withdrawalType === 'early_allowed' && (
            <>
              <div className="admin-detail-row">
                <div className="admin-detail-label">Penalty Applies</div>
                <div className="admin-detail-value">
                  <span className={`status ${bid.penaltyApplies ? 'status-warning' : 'status-approved'}`}>
                    {bid.penaltyApplies ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
              
              {bid.penaltyApplies && bid.penaltyDescription && (
                <div className="admin-detail-row">
                  <div className="admin-detail-label">Penalty Description</div>
                  <div className="admin-detail-value">{bid.penaltyDescription}</div>
                </div>
              )}
            </>
          )}

          <div className="admin-detail-row">
            <div className="admin-detail-label">Allocated Amount</div>
            <div className="admin-detail-value">
              {bid.allocatedAmount ? formatCurrency(bid.allocatedAmount, bid.currency) : 'Not allocated'}
            </div>
          </div>
          
          <div className="admin-detail-row">
            <div className="admin-detail-label">Submission Date</div>
            <div className="admin-detail-value">
              {bid.createdAt ? formatDate(bid.createdAt) : 'N/A'}
            </div>
          </div>
          
          {bid.notes && (
            <div className="admin-detail-row">
              <div className="admin-detail-label">Notes</div>
              <div className="admin-detail-value">{bid.notes}</div>
            </div>
          )}
        </div>
      </div>
      
      {request && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Request Information</h3>
            <Link to={`/admin/requests/${request.id || request._id}`} className="admin-view-btn">
              View Request
            </Link>
          </div>
          <div className="admin-card-body">
            <div className="admin-detail-row">
              <div className="admin-detail-label">Request ID</div>
              <div className="admin-detail-value">#{request.requestId || 'N/A'}</div>
            </div>
            
            <div className="admin-detail-row">
              <div className="admin-detail-label">System ID</div>
              <div className="admin-detail-value">#{request._id || request.id || 'N/A'}</div>
            </div>
            
            <div className="admin-detail-row">
              <div className="admin-detail-label">Company</div>
              <div className="admin-detail-value">
                {request.company ? (
                  typeof request.company === 'object' ? (
                    <Link to={`/admin/organizations/${request.company._id || request.company.id || ''}`}>
                      {request.company.name || 'View Company'}
                    </Link>
                  ) : (
                    <Link to={`/admin/organizations/${request.company}`}>
                      View Company
                    </Link>
                  )
                ) : 'N/A'}
              </div>
            </div>

            <div className="admin-detail-row">
              <div className="admin-detail-label">Currency</div>
              <div className="admin-detail-value">{request.currency || 'EUR'}</div>
            </div>
            
            <div className="admin-detail-row">
              <div className="admin-detail-label">Request Amount</div>
              <div className="admin-detail-value">{formatCurrency(request.amount, request.currency)}</div>
            </div>
            
            <div className="admin-detail-row">
              <div className="admin-detail-label">Status</div>
              <div className="admin-detail-value">
                <span className={`status status-${request.status || 'unknown'}`}>
                  {(request.status || 'unknown').replace('_', ' ')}
                </span>
              </div>
            </div>
            
            <div className="admin-detail-row">
              <div className="admin-detail-label">Duration</div>
              <div className="admin-detail-value">
                {request.duration === 'overnight' ? 'Overnight' : `${request.duration || 'N/A'} months`}
              </div>
            </div>

            <div className="admin-detail-row">
              <div className="admin-detail-label">Withdrawal Type</div>
              <div className="admin-detail-value">
                {request.withdrawalType === 'demand' ? 'Demand' :
                request.withdrawalType === 'early_allowed' ? 'Early Withdrawal Allowed' :
                request.withdrawalType === 'no_withdrawal' ? 'No Withdrawal Allowed' :
                'Not specified'}
              </div>
            </div>

          </div>
        </div>
      )}
      
      {bid.status === 'pending' && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Actions</h3>
          </div>
          <div className="admin-card-body">
            <div className="admin-actions">
              <button
                className="btn btn-primary"
                onClick={() => handleUpdateStatus('approved')}
                disabled={actionInProgress}
              >
                {actionInProgress ? 'Processing...' : 'Approve Bid'}
              </button>
              <button
                className="btn btn-danger"
                onClick={() => handleUpdateStatus('rejected')}
                disabled={actionInProgress}
              >
                {actionInProgress ? 'Processing...' : 'Reject Bid'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBidDetails;